import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import { AuthContext } from '@/lib/auth/models/auth.model';
import { Tables } from '@/integrations/supabase/types';
import { ItineraryData, ItineraryStatus } from '@/lib/builder/models/itinerary.model';
import { DayActivity } from '@/lib/builder/models/activity.model';


export type Itinerary = Tables<'itineraries'> & {
  pref_email?: string;
  pref_name?: string;
  pref_surname?: string;
  td_email?: string;
  td_name?: string;
  td_surname?: string;
  contact_email: string;
  contact_first_name?: string;
  contact_last_name?: string;
  travel_request_id: string;
};

interface UseItinerariesOptions {
  statusFilter?: string;
  excludeStatus?: string;
  disableAutoFetch?: boolean; // Nuovo parametro per disabilitare il caricamento automatico
}

export const useItineraries = (options: UseItinerariesOptions = {}) => {
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Usa useRef per memorizzare i valori delle opzioni e evitare loop infiniti
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Recupera l'utente dal contesto di autenticazione
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useItineraries deve essere usato all\'interno di un AuthProvider');
  }

  // Recupera gli itinerari dell'utente corrente
  const fetchItineraries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Query per ottenere gli itinerari utilizzando la view
      let query = supabase
        .from('complete_itinerary_info')
        .select('*');

      // Applica filtro per status se fornito
      if (optionsRef.current.statusFilter) {
        query = query.eq('status', optionsRef.current.statusFilter);
      }

      // Esclude status specificati se fornito
      if (optionsRef.current.excludeStatus) {
        query = query.neq('status', optionsRef.current.excludeStatus);
      }

      const { data, error: fetchError } = await query.order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching itineraries:', fetchError);
        setError('Errore nel caricamento degli itinerari');
        toast({
          title: "Errore",
          description: "Impossibile caricare gli itinerari",
          variant: "destructive",
        });
        return;
      }

      setItineraries(data || []);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Errore imprevisto nel caricamento degli itinerari');
      toast({
        title: "Errore",
        description: "Errore imprevisto nel caricamento degli itinerari",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Carica gli itinerari al mount e quando le opzioni cambiano (solo se non disabilitato)
  useEffect(() => {
    if (!options.disableAutoFetch) {
      fetchItineraries();
    } else {
      // Se il caricamento automatico è disabilitato, imposta loading a false
      setLoading(false);
    }
  }, [options.statusFilter, options.excludeStatus, options.disableAutoFetch, fetchItineraries]);

  // Funzione per aggiornare i dati
  const refreshData = useCallback(async () => {
    await fetchItineraries();
  }, [fetchItineraries]);

  // Funzione per salvare/aggiornare un itinerario
  const saveItinerary = useCallback(async (itineraryData: ItineraryData) => {
    try {
      let result;

      if (itineraryData.id) {

        const { data, error } = await supabase
          .from('itineraries')
          .update(itineraryData.getPayload())
          .eq('id', itineraryData.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating itinerary:', error);
          toast({
            title: "Errore",
            description: "Impossibile aggiornare l'itinerario",
            variant: "destructive",
          });
          return null;
        }

        result = data;

        // Aggiorna la lista locale
        setItineraries(prev => prev.map(itinerary =>
          itinerary.id === data.id ? { ...itinerary, ...data } : itinerary
        ));
      } else {

        const { data, error } = await supabase
          .from('itineraries')
          .insert(itineraryData.getPayload())
          .select()
          .single();

        if (error) {
          console.error('Error creating itinerary:', error);
          toast({
            title: "Errore",
            description: "Impossibile creare l'itinerario",
            variant: "destructive",
          });
          return null;
        }

        result = data;

        // Aggiorna la lista locale
        setItineraries(prev => [data, ...prev]);
      }

      return result;
    } catch (err) {
      console.error('Unexpected error:', err);
      toast({
        title: "Errore",
        description: "Errore imprevisto nel salvataggio dell'itinerario",
        variant: "destructive",
      });
      return null;
    }
  }, []);

  // Funzione per eliminare un itinerario
  const deleteItinerary = useCallback(async (itineraryId: string) => {
    try {
      const { error } = await supabase
        .from('itineraries')
        .delete()
        .eq('id', itineraryId);

      if (error) {
        console.error('Error deleting itinerary:', error);
        toast({
          title: "Errore",
          description: "Impossibile eliminare l'itinerario",
          variant: "destructive",
        });
        return false;
      }

      // Aggiorna la lista locale
      setItineraries(prev => prev.filter(itinerary => itinerary.id !== itineraryId));

      toast({
        title: "Itinerario eliminato",
        description: "L'itinerario è stato eliminato con successo",
      });

      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      toast({
        title: "Errore",
        description: "Errore imprevisto nell'eliminazione dell'itinerario",
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to fetch existing itinerary by requestId from database
  const fetchItinerary = useCallback(async (requestId: string) => {
    try {
      const { data, error } = await supabase
        .from('itineraries')
        .select('*')
        .eq('travel_request_id', requestId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching itinerary:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Unexpected error fetching itinerary:', error);
      return null;
    }
  }, []);

  // Function to fetch itinerary activities
  const fetchItineraryActivities = useCallback(async (itineraryId: string) => {
    try {
      const { data, error } = await supabase
        .from('itinerary_activities')
        .select('*')
        .eq('itinerary_id', itineraryId)
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching itinerary activities:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Unexpected error fetching itinerary activities:', error);
      return [];
    }
  }, []);

  // Function to save itinerary activities
  const saveItineraryActivities = useCallback(async (itineraryId: string, activities: DayActivity[]) => {
    try {
      // First, delete all existing activities for this itinerary
      const { error: deleteError } = await supabase
        .from('itinerary_activities')
        .delete()
        .eq('itinerary_id', itineraryId);

      if (deleteError) {
        console.error('Error deleting existing activities:', deleteError);
        toast({
          title: "Errore",
          description: "Impossibile eliminare le attività esistenti",
          variant: "destructive",
        });
        return false;
      }

      // If no activities to save, return success
      if (!activities || activities.length === 0) {
        return true;
      }

      // Filter out activities with empty or invalid dates
      // rimuovo anche le attività di check-out e sistemo le attività di check-in togliendo l'id fittizio
      const validActivities = activities.filter(activity => {
        if ((activity.id.startsWith('temp-') && activity.id.endsWith('-out')) || activity.type === 'accommodationOut') {
          return false;
        }
        if (activity.type === 'accommodationIn') {
          activity.type = 'accommodation';
          activity.id = null;
        }

        if (!activity.date || activity.date.trim() === '') {
          console.warn('Skipping activity with empty date:', activity.title);
          return false;
        }
        // Validate date format (YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(activity.date)) {
          console.warn('Skipping activity with invalid date format:', activity.title, activity.date);
          return false;
        }
        return true;
      });

      // Prepare activities for insertion
      const activitiesToInsert = validActivities.map(activity => ({
        itinerary_id: itineraryId,
        title: activity.title || '',
        description: activity.description || '',
        location: activity.location || '',
        type: activity.type || 'activity',
        date: activity.date,
        include_price: activity.include_price || false,
        cost: 'cost' in activity ? activity.cost || 0 : 0,
        link: activity.link || null,
        address: 'address' in activity ? activity.address || null : null,
        start_date: 'start_date' in activity ? activity.start_date || null : null,
        end_date: 'end_date' in activity ? activity.end_date || null : null,
        start_time: 'start_time' in activity ? activity.start_time || null : null,
        end_time: 'end_time' in activity ? activity.end_time || null : null,
        check_in_time: 'check_in_time' in activity ? activity.check_in_time || null : null,
        check_out_time: 'check_out_time' in activity ? activity.check_out_time || null : null,
        check_in_note: 'check_in_note' in activity ? activity.check_in_note || null : null,
        refundable_rate: 'refundable_rate' in activity ? activity.refundable_rate || false : false,
        price_tag: 'price_tag' in activity ? activity.price_tag || null : null,
        transport_type: 'transport_type' in activity ? activity.transport_type || null : null,
        company: 'company' in activity ? activity.company || null : null,
        media: 'media' in activity ? activity.media || [] : [],
        documentation: activity.documentation || [],
        travels: 'travels' in activity ? activity.travels || [] : []
      }));

      // If no valid activities to save after filtering, return success
      if (activitiesToInsert.length === 0) {
        console.log('No valid activities to save after filtering');
        return true;
      }

      // Insert new activities
      const { error: insertError } = await supabase
        .from('itinerary_activities')
        .insert(activitiesToInsert);

      if (insertError) {
        console.error('Error saving activities:', insertError);
        toast({
          title: "Errore",
          description: "Impossibile salvare le attività",
          variant: "destructive",
        });
        return false;
      }

      return true;
    } catch (err) {
      console.error('Unexpected error saving activities:', err);
      toast({
        title: "Errore",
        description: "Errore imprevisto nel salvataggio delle attività",
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to update itinerary status
  const updateItineraryStatus = useCallback(async (itineraryId: string, newStatus: ItineraryStatus) => {
    try {
      const { error } = await supabase
        .from('itineraries')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', itineraryId);

      if (error) {
        console.error('Error updating itinerary status:', error);
        toast({
          title: "Errore",
          description: "Impossibile aggiornare lo status dell'itinerario",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setItineraries(prev => prev.map(itinerary =>
        itinerary.id === itineraryId
          ? { ...itinerary, status: newStatus, updated_at: new Date().toISOString() }
          : itinerary
      ));

      return true;
    } catch (err) {
      console.error('Unexpected error updating itinerary status:', err);
      toast({
        title: "Errore",
        description: "Errore imprevisto nell'aggiornamento dello status",
        variant: "destructive",
      });
      return false;
    }
  }, []);

  // Function to duplicate an itinerary with all its activities (truly transactional)
  const duplicateItinerary = useCallback(async (itineraryId: string) => {
    try {
      // First, get the original itinerary to get the title
      const { data: originalItinerary, error: fetchError } = await supabase
        .from('itineraries')
        .select('title')
        .eq('id', itineraryId)
        .single();

      if (fetchError) {
        console.error('Error fetching original itinerary:', fetchError);
        toast({
          title: "Errore",
          description: "Impossibile recuperare l'itinerario originale",
          variant: "destructive",
        });
        return null;
      }

      // Use the transactional RPC function
      const { data: result, error: rpcError } = await supabase.rpc('duplicate_itinerary', {
        p_itinerary_id: itineraryId,
        p_new_title: `${originalItinerary.title} (Copy)`
      });

      if (rpcError) {
        console.error('Error in transactional duplicate:', rpcError);
        toast({
          title: "Errore",
          description: "Impossibile duplicare l'itinerario",
          variant: "destructive",
        });
        return null;
      }

      // Update local state with the new itinerary
      if (result && result.new_itinerary) {
        const newItinerary = result.new_itinerary as unknown as Itinerary;
        setItineraries(prev => [newItinerary, ...prev]);
        return newItinerary;
      }

      return null;
    } catch (err) {
      console.error('Unexpected error duplicating itinerary:', err);
      toast({
        title: "Errore",
        description: "Errore imprevisto nella duplicazione dell'itinerario",
        variant: "destructive",
      });
      return null;
    }
  }, []);

  return {
    itineraries,
    loading,
    error,
    refreshData,
    saveItinerary,
    deleteItinerary,
    fetchItinerary,
    fetchItineraryActivities,
    saveItineraryActivities,
    duplicateItinerary,
    updateItineraryStatus
  };
};
